import { observer } from 'mobx-react';
import { useBaseStore } from '@roc/feature-app-core';
import { DocumentForms, useDocumentStore } from '@roc/feature-documents';
import { LoanProcess } from '@roc/feature-utils';
import { TaskV2Type } from '@roc/feature-general-tasks';
import { mapStatusFilter, SHOW_ALL_TO_DOS, StatusFilterField, TaskTodoCardCompponent, TodoStatus } from '@roc/feature-loan-details';
import { useEffect, useRef, useState } from 'react';
import { Button, PDFPreview, PrintableArea, PrintableAreaRef } from '@roc/ui';
import { Box, Tooltip } from '@material-ui/core';
import { PhotoCamera, UnfoldLess, UnfoldMore } from '@material-ui/icons';

interface PayoffTodosProps {
  loanId: number;
  payoffId: number;
  isCPortal?: boolean;
}

export const PayoffTodos = observer(({ loanId, payoffId, isCPortal }: PayoffTodosProps) => {
  const { documentStore } = useDocumentStore();

  const [statusFilter, setStatusFilter] = useState(SHOW_ALL_TO_DOS);
  const [filterBy, setFilterBy] = useState(mapStatusFilter[statusFilter]);
  const currentForm = documentStore.documentFormStore.currentForm;
  const loanProcess = LoanProcess.PAYOFF;
  const { globalStore } = useBaseStore();
  const printableAreaRef = useRef<PrintableAreaRef>(null);
  const [expandAll, setExpandAll] = useState(0);
  const [collapseAll, setCollapseAll] = useState(0);
  const isInternalPortal = globalStore.isInternalPortal;
  const todos =
    documentStore.loanTodosStore.todosByLoanProcess[loanProcess] ??
    [];

  const loadTodos = () => {
    documentStore.loanTodosStore.fetchTodos(loanId, loanProcess, TaskV2Type.PAYOFF_ID, payoffId);
  };

  const download = () => {
    printableAreaRef.current.downloadAsPdf(`Loan ${loanId} Payoff ${payoffId} Documents.pdf`, {
      singlePage: true,
    });
  };

  useEffect(() => {
    if (payoffId) {
      documentStore.loanTodosStore.setPrioritiesFilter([]);
      loadTodos();
    }
  }, [loanId, payoffId]);

  useEffect(() => {
    if (!currentForm && todos.length > 0 && payoffId) {
      loadTodos();
    }
  }, [currentForm]);

  const payoffTodos = todos.filter(t => (isInternalPortal || t?.document?.status !== TodoStatus.NOT_APPLICABLE));

  const filteredTodos = payoffTodos.filter(t => {

    if (filterBy === TodoStatus.ALL) return t;
    if (filterBy === TodoStatus.PENDING) return t.status === filterBy || t.status === TodoStatus.MORE_INFO_NEEDED;
    return t.status === filterBy || filterBy.split(',').includes(t.status);
  });

  const onStatusFilterChange = (val) => {
    setStatusFilter(val);
    setFilterBy(mapStatusFilter[val]);
  }

  return (
    <Box>
      <Box pb={2} display="flex" justifyContent="flex-end">
        <Box pr={2}>
          <Tooltip title="Expand all sections" placement='bottom'>
            <div>
              <Button testId="expandAll" onClick={() => {
                setExpandAll(x => x + 1);
              }} variant="outlined" style={{
                letterSpacing: 'normal',
                textTransform: 'none',
              }}>
                <Box height={'31px'} display="flex" alignItems="center">
                  <UnfoldMore />&nbsp;&nbsp;Expand All
                </Box>
              </Button>
            </div>
          </Tooltip>
        </Box>
        <Box pr={2}>
          <Tooltip title="Collapse all sections" placement='bottom'>
            <div>
              <Button testId="collapseAll" onClick={() => {
                setCollapseAll(x => x + 1);
              }} variant="outlined" style={{
                letterSpacing: 'normal',
                textTransform: 'none',
              }}>
                <Box height={'31px'} display="flex" alignItems="center">
                  <UnfoldLess />&nbsp;&nbsp;Collapse All
                </Box>
              </Button>
            </div>
          </Tooltip>
        </Box>
        <Box pr={2}>
          <Tooltip title="Take screenshot" placement='bottom'>
            <div>
              <Button testId="downloadAll" onClick={download} variant="outlined" style={{
                letterSpacing: 'normal',
                textTransform: 'none',
              }}>
                <Box height={'31px'} display="flex" alignItems="center">
                  <PhotoCamera />&nbsp;&nbsp;Take Screenshot
                </Box>
              </Button>
            </div>
          </Tooltip>
        </Box>
        <StatusFilterField
          value={statusFilter}
          onChange={onStatusFilterChange}
          version="v2"
        />
      </Box>
      <PrintableArea ref={printableAreaRef}>
        <TaskTodoCardCompponent
          loanProcess={loanProcess}
          todos={filteredTodos}
          showSectionActions
          showDateColumn={true}
          showStatusColumn={true}
          showAssigneeColumn={isInternalPortal}
          showFileName
          expandAll={expandAll}
          collapseAll={collapseAll}
          showDownloadTodo
          showActions={true}
          filteredBy={filterBy}
        />
      </PrintableArea>
      <DocumentForms loanId={loanId?.toString()} />
      <PDFPreview
        open={documentStore.documentPreview != null}
        title={documentStore.documentPreview?.title}
        data={documentStore.documentPreview?.data}
        dataV2={documentStore.documentPreview?.data}
        headers={documentStore.documentPreview?.headers}
        onClose={() => documentStore.closeDocumentPreview()}
      />
    </Box>
  );
});
